export type ElectionMapReading = 'leader' | 'counted' | 'margin';

export interface ViewerCandidate {
  external_id?: string;
  number?: string;
  name: string;
  party: string;
  votes: number;
  percent: number | null;
  photo_url?: string;
  site_url?: string;
  elected?: boolean;
  status?: string;
}

export interface ViewerStateResult {
  uf: string;
  available: boolean;
  candidates: ViewerCandidate[];
  sections?: { totalized?: number | null; total?: number | null; percent?: number | null };
  valid_votes?: number | null;
  source_url?: string;
  message?: string;
  updated_at?: { date?: string; time?: string };
}

export const viewerUfNames: Record<string, string> = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará', DF: 'Distrito Federal',
  ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais', PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí',
  RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul', RO: 'Rondônia',
  RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins',
};

export const viewerIbgeUfs: Record<string, string> = {
  '11': 'RO', '12': 'AC', '13': 'AM', '14': 'RR', '15': 'PA', '16': 'AP', '17': 'TO',
  '21': 'MA', '22': 'PI', '23': 'CE', '24': 'RN', '25': 'PB', '26': 'PE', '27': 'AL', '28': 'SE', '29': 'BA',
  '31': 'MG', '32': 'ES', '33': 'RJ', '35': 'SP', '41': 'PR', '42': 'SC', '43': 'RS', '50': 'MS', '51': 'MT', '52': 'GO', '53': 'DF',
};

const partyColors: Record<string, string> = {
  PT: '#dc3346', PL: '#3265df', MDB: '#168267', PSD: '#7a9e49', PP: '#38a4bf', PSB: '#ce8c24',
  PSDB: '#2c83a7', PDT: '#b9516c', UNIÃO: '#7256a1', UNIAO: '#7256a1', REPUBLICANOS: '#b06b30',
  PSOL: '#a169b5', NOVO: '#e87524', REDE: '#428d74', PODE: '#7375ac', SOLIDARIEDADE: '#ba7048',
};

export function viewerCandidateColor(candidate?: Pick<ViewerCandidate, 'name' | 'party'>, fallback = '#65828b'): string {
  if (!candidate) return fallback;
  const name = candidate.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  if (name === 'FLAVIO NANTES BOLSONARO') return '#3265df';
  if (name === 'LUIZ INACIO LULA DA SILVA') return '#dc3346';
  return partyColors[candidate.party.toUpperCase()] ?? fallback;
}

export function rankViewerCandidates(candidates: ViewerCandidate[] = []): ViewerCandidate[] {
  return [...candidates].sort((a, b) => b.votes - a.votes);
}

export function viewerLeader(candidates: ViewerCandidate[] = []): ViewerCandidate | null {
  const [first, second] = rankViewerCandidates(candidates);
  return first && first.votes > 0 && first.votes !== second?.votes ? first : null;
}

function blendWithWhite(hex: string, strength: number): string {
  const amount = Math.max(0, Math.min(1, strength));
  return `rgb(${[1, 3, 5].map((offset) => Math.round(255 + (parseInt(hex.slice(offset, offset + 2), 16) - 255) * amount)).join(', ')})`;
}

export function electionAreaColor(
  available: boolean,
  candidates: ViewerCandidate[] | undefined,
  counted: number | null | undefined,
  reading: ElectionMapReading,
): string {
  if (!available) return '#e4e9e7';
  if (reading === 'counted') return counted != null && Number.isFinite(counted) ? blendWithWhite('#16734f', .12 + Math.max(0, Math.min(100, counted)) / 100 * .88) : '#e4e9e7';
  const leader = viewerLeader(candidates);
  if (!leader) return '#e4e9e7';
  const color = viewerCandidateColor(leader);
  if (reading === 'leader') return color;
  const [first, second] = rankViewerCandidates(candidates);
  if (first?.percent == null || second?.percent == null) return '#e4e9e7';
  const margin = Math.max(0, first.percent - second.percent);
  return blendWithWhite(color, .2 + Math.min(50, margin) / 50 * .8);
}

export function viewerRate(value: number | null | undefined, digits = 1): string {
  return value != null && Number.isFinite(value)
    ? `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)}%`
    : '—';
}

export function compareRoundCandidates(current: ViewerCandidate[], previous: ViewerCandidate[]): Array<{ candidate: ViewerCandidate; delta: number }> {
  return current.flatMap((candidate) => {
    const matched = candidate.external_id ? previous.find((older) => older.external_id === candidate.external_id) : undefined;
    return matched?.percent != null && candidate.percent != null ? [{ candidate, delta: candidate.percent - matched.percent }] : [];
  });
}

